import React, { useState, Fragment } from 'react';
import { Loan, SortState, SortField, Role } from '../types/loan';
import { ChevronUpIcon, ChevronDownIcon, SearchIcon, ChevronRightIcon, LightbulbIcon, ZapIcon } from 'lucide-react';
interface LoanTableProps {
  loans: Loan[];
  currentMarketRate: number;
  selectedLoanIds: Set<string>;
  onToggleSelection: (loanId: string) => void;
  onToggleAll: (selectAll: boolean) => void;
  sortState: SortState;
  onSort: (field: SortField) => void;
  onTakeAction: (loan: Loan) => void;
  role: Role;
}
export function LoanTable({
  loans,
  currentMarketRate,
  selectedLoanIds,
  onToggleSelection,
  onToggleAll,
  sortState,
  onSort,
  onTakeAction,
  role
}: LoanTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const allSelected = loans.length > 0 && selectedLoanIds.size === loans.length;
  const someSelected = selectedLoanIds.size > 0 && selectedLoanIds.size < loans.length;
  const toggleRowExpansion = (loanId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  };
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getRateColorClass = (diff: number) => {
    if (diff > 2.0) return 'text-red-700 bg-red-50 ring-red-600/20';
    if (diff >= 1.0) return 'text-orange-700 bg-orange-50 ring-orange-600/20';
    return 'text-green-700 bg-green-50 ring-green-600/20';
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortState.field !== field) return <ChevronDownIcon className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-154" />;
    return sortState.direction === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-violet-600" data-id="element-155" /> : <ChevronDownIcon className="w-4 h-4 text-violet-600" data-id="element-156" />;
  };
  const thClass = 'px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors select-none';
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-157">
      <div className="overflow-x-auto" data-id="element-158">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-159">
          <thead className="bg-gray-50/80 border-b border-gray-200" data-id="element-160">
            <tr data-id="element-161">
              <th scope="col" className="px-4 py-3.5 w-10" data-id="element-162"></th>
              <th scope="col" className="px-4 py-3.5 text-left w-10" data-id="element-163">
                <input type="checkbox" className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer" checked={allSelected} ref={input => {
                if (input) input.indeterminate = someSelected;
              }} onChange={e => onToggleAll(e.target.checked)} data-id="element-164" />
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('borrowerName')} data-id="element-165">
                <div className="flex items-center gap-1" data-id="element-166">
                  Borrower <SortIcon field="borrowerName" data-id="element-167" />
                </div>
              </th>
              {role !== 'Loan Officer' && <th scope="col" className="px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-168">
                  Loan Officer
                </th>}
              <th scope="col" className="px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-169">
                Phone
              </th>
              <th scope="col" className="px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-170">
                Email
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('loanAmount')} data-id="element-171">
                <div className="flex items-center gap-1" data-id="element-172">
                  Loan Amount <SortIcon field="loanAmount" data-id="element-173" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('interestRate')} data-id="element-174">
                <div className="flex items-center gap-1" data-id="element-175">
                  Rate <SortIcon field="interestRate" data-id="element-176" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('rateDifference')} data-id="element-177">
                <div className="flex items-center gap-1" data-id="element-178">
                  Above Market <SortIcon field="rateDifference" data-id="element-179" />
                </div>
              </th>
              <th scope="col" className={thClass} onClick={() => onSort('closeDate')} data-id="element-180">
                <div className="flex items-center gap-1" data-id="element-181">
                  Close Date <SortIcon field="closeDate" data-id="element-182" />
                </div>
              </th>
              <th scope="col" className="px-4 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-183">
                Target Rate
              </th>
              <th scope="col" className="px-4 py-3.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-184">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-185">
            {loans.length === 0 ? <tr data-id="element-186">
                <td colSpan={role !== 'Loan Officer' ? 13 : 12} className="px-6 py-16 text-center" data-id="element-187">
                  <div className="flex flex-col items-center justify-center" data-id="element-188">
                    <div className="text-gray-300 mb-3" data-id="element-189">
                      <SearchIcon className="w-10 h-10" data-id="element-190" />
                    </div>
                    <p className="text-gray-500 font-medium" data-id="element-191">
                      No loans found matching your criteria.
                    </p>
                    <p className="text-sm text-gray-400 mt-1" data-id="element-192">
                      Adjust your market rate, date range, or threshold above.
                    </p>
                  </div>
                </td>
              </tr> : loans.map(loan => {
            const rateDiff = loan.interestRate - currentMarketRate;
            const isSelected = selectedLoanIds.has(loan.id);
            const isExpanded = expandedRows.has(loan.id);
            return <Fragment key={loan.id} data-id="element-193">
                    <tr className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-violet-50/50' : ''}`} data-id="element-194">
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-195">
                        <button onClick={() => toggleRowExpansion(loan.id)} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" data-id="element-196">
                          <ChevronRightIcon className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-197" />
                        </button>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-198">
                        <input type="checkbox" className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500 cursor-pointer" checked={isSelected} onChange={() => onToggleSelection(loan.id)} data-id="element-199" />
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-200">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-201">
                          {loan.borrowerName}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-202">
                          {loan.id}
                        </div>
                      </td>
                      {role !== 'Loan Officer' && <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-600" data-id="element-203">
                          {loan.loanOfficer}
                        </td>}
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700" data-id="element-204">
                        {loan.phone}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700" data-id="element-205">
                        <a href={`mailto:${loan.email}`} className="text-violet-600 hover:text-violet-800 hover:underline" data-id="element-206">
                          {loan.email}
                        </a>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-207">
                        {formatCurrency(loan.loanAmount)}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-208">
                        {loan.interestRate.toFixed(3)}%
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-209">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset tabular-nums ${getRateColorClass(rateDiff)}`} data-id="element-210">
                          +{rateDiff.toFixed(3)}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-700 tabular-nums" data-id="element-211">
                        {formatDate(loan.closeDate)}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-sm tabular-nums" data-id="element-212">
                        {loan.targetRate ? <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset text-emerald-700 bg-emerald-50 ring-emerald-600/20" data-id="element-213">
                            {loan.targetRate.toFixed(3)}%
                          </span> : <span className="text-gray-400" data-id="element-214">—</span>}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right text-sm font-medium" data-id="element-215">
                        <button onClick={() => onTakeAction(loan)} className="inline-flex items-center gap-1.5 text-white bg-gradient-to-r from-violet-600 to-cyan-500 hover:brightness-110 px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-sm" data-id="element-216">
                          <ZapIcon className="w-3.5 h-3.5" data-id="element-217" />
                          <span data-id="element-218">Take Action</span>
                        </button>
                      </td>
                    </tr>
                    {isExpanded && <tr className="bg-gray-50/80 border-t border-gray-100" data-id="element-219">
                        <td colSpan={role !== 'Loan Officer' ? 13 : 12} className="px-8 py-4" data-id="element-220">
                          <div className="flex items-start gap-3" data-id="element-221">
                            <div className="bg-amber-100 text-amber-600 p-1.5 rounded-md mt-0.5" data-id="element-222">
                              <LightbulbIcon className="w-4 h-4" data-id="element-223" />
                            </div>
                            <div data-id="element-224">
                              <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-225">
                                Refinance Talking Points
                              </h4>
                              <ul className="space-y-1.5" data-id="element-226">
                                {loan.talkingPoints.map((point, idx) => <li key={idx} className="text-sm text-gray-600 flex items-start gap-2" data-id="element-227">
                                    <span className="text-amber-500 mt-1" data-id="element-228">
                                      •
                                    </span>
                                    {point}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </div>;
}